import { useEffect, useRef, useState } from 'react'
import { trashPictures, type AdminLibrary } from '../services/libraryAdminService'
import './report.css'

/**
 * Delete to trash, beside a selection (Huanrui, 2026-10-15)
 * -------------------------------------------------------------
 * Where the red ! (ReportButton) was in Audit, Visual Language and Design
 * References; Report stays in the Internal Library only. Administrators only:
 * the caller shows it to them, and the Worker refuses anyone else.
 *
 * The same small round button that opens into a pill to ask first ("Trash
 * 3?"), the second click sends, and the question goes away by itself.
 *
 * THIS LIBRARY ONLY. A picture that is also in another of the big libraries
 * (in Design References and in Audit, say) leaves the one it was deleted
 * from and stays in the others, with their tags (routes/libraryAdmin.ts,
 * trashPictures). Restorable from the Review Queue's Trash for 30 days.
 */
export function TrashButton({
  assetIds,
  library,
  size = 'normal',
  onDone,
}: {
  assetIds: string[]
  library: AdminLibrary
  size?: 'normal' | 'small'
  /** After they have gone: clear the selection, say so. */
  onDone?: (trashed: number) => void
}) {
  const [state, setState] = useState<'idle' | 'asking' | 'sending' | 'failed'>('idle')
  const [note, setNote] = useState('')
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])
  const after = (ms: number, next: typeof state) => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setState(next), ms)
  }

  const count = assetIds.length
  const label = state === 'asking' ? `Trash ${count}?` : state === 'sending' ? 'Moving…' : state === 'failed' ? 'Not moved' : ''

  return (
    <button
      type="button"
      className={`ww-report ww-report--${size}${state !== 'idle' ? ' ww-report--open' : ''}`}
      disabled={count === 0 || state === 'sending'}
      title={
        state === 'failed'
          ? note
          : `Delete ${count} picture${count === 1 ? '' : 's'} to the trash, from this library only (restorable for 30 days)`
      }
      aria-label={label || 'Delete to trash'}
      onClick={() => {
        if (state === 'idle' || state === 'failed') {
          setState('asking')
          after(4000, 'idle')
          return
        }
        if (state !== 'asking') return
        window.clearTimeout(timer.current)
        setState('sending')
        trashPictures(library, assetIds)
          .then(({ trashed }) => {
            setState('idle')
            // Lists of pictures read themselves again; the Review Queue's Trash too.
            window.dispatchEvent(new Event('ww:library-changed'))
            window.dispatchEvent(new Event('ww:trash-changed'))
            onDone?.(trashed)
          })
          .catch((err: unknown) => {
            setNote(err instanceof Error ? err.message : 'Could not move them to the trash')
            setState('failed')
            after(4000, 'idle')
          })
      }}
    >
      <span className="ww-report__mark" aria-hidden="true">
        <svg width="10" height="10" viewBox="0 0 12 12" style={{ display: 'block', margin: '0 auto' }}>
          <path
            d="M2 3h8M4.5 3V2h3v1M3 3l.5 7h5L9 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      {label ? <span className="ww-report__text">{label}</span> : null}
    </button>
  )
}
